<template>
  <div class="iv-tab">
    <div v-if="loading" class="ds-empty">
      <p class="ds-empty__message">Video's laden…</p>
    </div>
    <div v-else-if="error" class="ds-empty">
      <p class="ds-empty__message">{{ error }}</p>
      <ds-button variant="ghost" data-qa="instructional-videos-retry-button" @click="load">
        Opnieuw proberen
      </ds-button>
    </div>

    <template v-else>
      <div class="iv-tab__head">
        <ds-button data-qa="iv-add-button" @click="startAdd">
          <ds-icon name="plus" />
          Nieuwe video toevoegen
        </ds-button>
      </div>

      <div v-if="videos.length === 0" class="ds-empty">
        <h2 class="ds-empty__title">Geen instructievideo's</h2>
        <p class="ds-empty__message">Voeg een video toe om te beginnen.</p>
      </div>

      <div v-else class="ds-resource-list">
        <div v-for="video in sortedVideos" :key="video.id" class="ds-resource-card">
          <div class="ds-resource-card__media">
            <iframe
              v-if="video.videoUrl"
              :src="video.videoUrl"
              :title="video.title || 'Instructievideo'"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            />
            <span v-else class="iv-tab__no-preview">Geen URL</span>
          </div>
          <div class="ds-resource-card__info">
            <div class="ds-resource-card__title">{{ video.title || '-' }}</div>
            <div class="ds-resource-card__meta">
              {{ video.width }}&times;{{ video.height }} &middot; Volgorde: {{ video.sortOrder }}
            </div>
            <div class="ds-resource-card__meta">{{ video.videoUrl }}</div>
          </div>
          <div class="ds-resource-card__actions">
            <button
              class="ds-icon-button"
              title="Bewerken"
              data-qa="iv-edit-button"
              @click="startEdit(video)"
            >
              <ds-icon name="edit" />
            </button>
            <button
              class="ds-icon-button"
              title="Verwijderen"
              data-qa="iv-delete-button"
              @click="confirmDelete(video)"
            >
              <ds-icon name="trash" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Add/Edit modal -->
    <ds-dialog
      v-if="showForm"
      id="iv-form"
      :title="formTitle"
      size="md"
      as="form"
      @close="cancelForm"
      @submit.prevent="saveForm"
    >
      <div class="ds-form">
        <div class="ds-form__field">
          <label for="iv-title">Titel</label>
          <input id="iv-title" v-model="form.title" type="text" class="ds-field" />
        </div>
        <div class="ds-form__field">
          <label for="iv-url">Video URL</label>
          <input id="iv-url" v-model="form.videoUrl" type="text" class="ds-field" />
        </div>
        <div class="ds-form__row">
          <div class="ds-form__field">
            <label for="iv-width">Breedte</label>
            <input id="iv-width" v-model.number="form.width" type="number" class="ds-field" />
          </div>
          <div class="ds-form__field">
            <label for="iv-height">Hoogte</label>
            <input id="iv-height" v-model.number="form.height" type="number" class="ds-field" />
          </div>
          <div class="ds-form__field">
            <label for="iv-sort-order">Volgorde</label>
            <input
              id="iv-sort-order"
              v-model.number="form.sortOrder"
              type="number"
              class="ds-field"
            />
          </div>
        </div>
        <div v-if="formError" class="ds-alert ds-alert--danger">{{ formError }}</div>
      </div>
      <template #footer>
        <ds-button variant="text" type="button" data-qa="iv-form-cancel" @click="cancelForm">
          Annuleren
        </ds-button>
        <ds-button type="submit" data-qa="iv-form-save" :disabled="formSaving">
          <ds-spinner v-if="formSaving" inline />
          <ds-icon v-else-if="formEditId" name="edit" />
          <ds-icon v-else name="save" />
          {{ formSaving ? 'Opslaan…' : formEditId ? 'Wijzig' : 'Opslaan' }}
        </ds-button>
      </template>
    </ds-dialog>

    <ds-confirm-dialog
      id="iv-delete"
      :show="!!deleteTarget"
      title="Video verwijderen"
      :body="deleteConfirmText"
      confirm-label="Verwijderen"
      cancel-label="Annuleren"
      danger
      :confirm-disabled="deleting"
      @confirm="executeDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import videosApi from '@/api/server/InstructionalVideos.api'

export default {
  name: 'InstructionalVideosTab',
  components: {
    DsButton,
    DsIcon,
    DsSpinner,
    DsDialog,
    DsConfirmDialog,
  },
  data() {
    return {
      videos: [],
      loading: false,
      error: null,

      showForm: false,
      formEditId: null,
      form: { title: '', videoUrl: '', width: 600, height: 300, sortOrder: 0 },
      formSaving: false,
      formError: null,

      deleteTarget: null,
      deleting: false,
    }
  },
  computed: {
    formTitle() {
      return this.formEditId ? 'Video bewerken' : 'Nieuwe video toevoegen'
    },
    deleteConfirmText() {
      return this.deleteTarget
        ? `Weet je zeker dat je de video "${this.deleteTarget.title}" wilt verwijderen?`
        : ''
    },
    sortedVideos() {
      return [...this.videos].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.videos = await videosApi.getInstructionalVideos()
      } catch {
        this.error = "Er is een fout opgetreden bij het ophalen van de video's."
      } finally {
        this.loading = false
      }
    },
    startAdd() {
      this.formEditId = null
      this.form = { title: '', videoUrl: '', width: 600, height: 300, sortOrder: 0 }
      this.formError = null
      this.showForm = true
    },
    startEdit(video) {
      this.formEditId = video.id
      this.form = {
        title: video.title || '',
        videoUrl: video.videoUrl || '',
        width: video.width || 640,
        height: video.height || 360,
        sortOrder: video.sortOrder || 0,
      }
      this.formError = null
      this.showForm = true
    },
    cancelForm() {
      this.showForm = false
      this.formError = null
    },
    async saveForm() {
      this.formSaving = true
      this.formError = null
      try {
        if (this.formEditId) {
          await videosApi.updateInstructionalVideo(this.formEditId, this.form)
        } else {
          await videosApi.addInstructionalVideo(this.form)
        }
        this.showForm = false
        await this.load()
      } catch {
        this.formError = 'Er is een fout opgetreden bij het opslaan van de video.'
      } finally {
        this.formSaving = false
      }
    },
    confirmDelete(video) {
      this.deleteTarget = video
    },
    async executeDelete() {
      this.deleting = true
      try {
        await videosApi.deleteInstructionalVideo(this.deleteTarget.id)
        this.deleteTarget = null
        await this.load()
      } catch {
        this.deleteTarget = null
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The cards, buttons, dialogs and form fields come from the
// design system (styles/ds/). See styles/ds/README.md before adding anything
// here.

.iv-tab__head {
  display: flex;
  justify-content: flex-start;
  margin-bottom: var(--ds-space-4);
}

.iv-tab__no-preview {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-color-text-subtle);
}
</style>
